import graphql from "babel-plugin-relay/macro";
import { uniqBy } from "lodash";
import { useEffect } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { PatronEditBooking } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/types";
import { JoinBooking } from "@/screens/JoinBooking/types";
import { DropdownSelectOptionsGroup } from "@whoosh/design-system/src/components/controls/DropdownSelect";
import { HoleCount } from "@whoosh/design-system/src/components/icons_v2/courseLayout";
import { GetTransportIconFromCode } from "@whoosh/design-system/src/components/icons_v2/transportation";
import { HStack, Text } from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { useGetBundledRateOptionsQuery } from "./__generated__/useGetBundledRateOptionsQuery.graphql";
import { TransportationTypeOption } from "./types/transportationTypeOption";

export const isGolfRound = (
  round: PatronEditBooking.Round | JoinBooking.CustomerRound | null
): round is PatronEditBooking.GolfRound => {
  return round?.type === "Golf";
};

export type BundledRateType = "CUSTOMER" | "MEMBER" | "GUEST";
export type BundledTransportationPlayerType = "CUSTOMER" | "MEMBER" | "GUEST";

type UseBundledRateOptionsQueryArgs = {
  timeSlotId?: string | null;
  membershipTypeId?: string | null;
  visibleToMembershipTypeId?: string | null;
  rateType?: BundledRateType | null;
  nonPlayingHost?: boolean;
  transportationMemberId?: string | null;
  transportationPlayerType?: BundledTransportationPlayerType | null;
};

type RateOption = {
  id: string;
  name: string;
  nineHolePrice: number;
  eighteenHolePrice: number;
};

export const getBundledRateTypeFromClientType = (
  clientType?: PatronEditBooking.RoundPlayer["clientType"] | null
): BundledRateType => {
  if (clientType === "member" || clientType === "memberHold") {
    return "MEMBER";
  }
  if (
    clientType === "guest" ||
    clientType === "newGuest" ||
    clientType === "unknownGuest"
  ) {
    return "GUEST";
  }
  return "CUSTOMER";
};

export const getBundledTransportationPlayerTypeFromClientType = (
  clientType?: PatronEditBooking.RoundPlayer["clientType"] | null
): BundledTransportationPlayerType => {
  if (clientType === "member" || clientType === "memberHold") {
    return "MEMBER";
  }
  if (clientType === "customer" || clientType === "unknownCustomer") {
    return "CUSTOMER";
  }
  return "GUEST";
};

export const useBundledRateOptionsQuery = ({
  timeSlotId,
  membershipTypeId,
  visibleToMembershipTypeId,
  rateType,
  nonPlayingHost,
  transportationMemberId,
  transportationPlayerType,
}: UseBundledRateOptionsQueryArgs) => {
  const [queryRef, loadQuery, disposeQuery] =
    useQueryLoader<useGetBundledRateOptionsQuery>(
      useGetBundledRateOptionsQueryString
    );

  useEffect(() => {
    if (!timeSlotId || !rateType) {
      disposeQuery();
      return;
    }

    loadQuery(
      {
        timeSlotId,
        membershipTypeId,
        visibleToMembershipTypeId,
        rateType,
        nonPlayingHost,
        transportationMemberId,
        transportationPlayerType,
      },
      { fetchPolicy: "store-and-network" }
    );
  }, [
    disposeQuery,
    loadQuery,
    membershipTypeId,
    nonPlayingHost,
    rateType,
    timeSlotId,
    transportationMemberId,
    transportationPlayerType,
    visibleToMembershipTypeId,
  ]);

  return { queryRef };
};

export const useGetBundledRateOptions = (
  currentHoleCount: HoleCount,
  queryRef: PreloadedQuery<useGetBundledRateOptionsQuery>,
  currentRound: PatronEditBooking.Round | JoinBooking.CustomerRound | null,
  transportationTypesOverride?: TransportationTypeOption[]
) => {
  const { currencySymbol } = useGetClubCurrency();
  const data = usePreloadedQuery<useGetBundledRateOptionsQuery>(
    useGetBundledRateOptionsQueryString,
    queryRef
  );
  const isPrivateClub =
    data?.slot?.agenda?.facility?.club?.supportsPrivate ?? false;

  const clientType =
    isGolfRound(currentRound) && currentRound.player?.clientType
      ? currentRound.player.clientType
      : null;

  const rates =
    data?.slot?.rates?.flatMap((playerRate) => {
      if (!playerRate?.rate?.id || !playerRate.rate.name) {
        return [];
      }

      const visibility = playerRate.rate.visibility;
      const appliesToMembershipType =
        playerRate.rate.appliesToMembershipType ?? false;

      if (visibility === "NONE") {
        return [];
      }

      if (visibility === "QUALIFYING" && !appliesToMembershipType) {
        return [];
      }

      return [
        {
          id: playerRate.rate.id,
          name: playerRate.rate.name,
          nineHolePrice: playerRate.nineHolePrice ?? 0,
          eighteenHolePrice: playerRate.eighteenHolePrice ?? 0,
        },
      ];
    }) ?? [];

  // Only access bookedRate and roundRate if currentRound is a GolfRound
  const bookedRate = isGolfRound(currentRound) ? currentRound.bookedRate : null;
  const roundRate = isGolfRound(currentRound) ? currentRound.roundRate : null;
  const memberRate = roundRate ?? bookedRate;
  const isMemberClient = clientType === "member" || clientType === "memberHold";

  // override matching playerRate with the bookedRate and roundRate
  // IF the bookedRate and roundRate are valid, then use them
  const finalRates = Array.from(rates).map((rr) => {
    if (bookedRate && rr?.id === bookedRate.id && bookedRate.name) {
      return {
        id: bookedRate.id,
        name: bookedRate.name,
        nineHolePrice: bookedRate.nineHolePrice ?? rr.nineHolePrice ?? 0,
        eighteenHolePrice:
          bookedRate.eighteenHolePrice ?? rr.eighteenHolePrice ?? 0,
      };
    }
    if (roundRate && rr?.id === roundRate.id && roundRate.name) {
      return {
        id: roundRate.id,
        name: roundRate.name,
        nineHolePrice: roundRate.nineHolePrice ?? rr.nineHolePrice ?? 0,
        eighteenHolePrice:
          roundRate.eighteenHolePrice ?? rr.eighteenHolePrice ?? 0,
      };
    }
    return rr;
  });
  const finalRateIds = new Set(finalRates.map((rate) => rate.id));

  const appendableRates: RateOption[] = [bookedRate, roundRate].flatMap(
    (rate) => {
      if (!rate?.id || !rate.name || finalRateIds.has(rate.id)) {
        return [];
      }

      return [
        {
          id: rate.id,
          name: rate.name,
          nineHolePrice: rate.nineHolePrice ?? 0,
          eighteenHolePrice: rate.eighteenHolePrice ?? 0,
        },
      ];
    }
  );

  const rateOptionsToUse = uniqBy(
    isPrivateClub && isMemberClient
      ? memberRate
        ? [
            {
              id: memberRate.id,
              name: memberRate.name ?? "",
              nineHolePrice: memberRate.nineHolePrice ?? 0,
              eighteenHolePrice: memberRate.eighteenHolePrice ?? 0,
            },
          ]
        : []
      : [...finalRates, ...appendableRates],
    "id"
  );

  const queryTransportationTypes =
    data?.slot?.transportationTypes?.flatMap((tt) => (tt ? [tt] : [])) ?? [];
  const transportationTypes =
    transportationTypesOverride && transportationTypesOverride.length > 0
      ? transportationTypesOverride
      : queryTransportationTypes;

  const bundledOptions: DropdownSelectOptionsGroup<string>[] =
    rateOptionsToUse.map((rate) => {
      const ratePriceForHoleCount =
        (currentHoleCount === "EIGHTEEN"
          ? rate.eighteenHolePrice
          : rate.nineHolePrice) || 0;

      const transportationTypeOptions = transportationTypes.map((tt) => {
        const transportationTypePriceForHoleCount =
          (currentHoleCount === "EIGHTEEN"
            ? tt.eighteenHolePrice
            : tt.nineHolePrice) || 0;

        const totalCost =
          ratePriceForHoleCount + transportationTypePriceForHoleCount;
        const totalCostLabel =
          !isPrivateClub && totalCost
            ? `${currencySymbol}${ConvertCentsToDollars(totalCost)}`
            : "";
        const combinedIds = `${rate.id}.${tt.id}`;

        const transportationIcon = GetTransportIconFromCode(
          tt.customCode || tt?.code || "",
          "mainTiny"
        );

        return {
          value: combinedIds,
          leftElement: transportationIcon ? transportationIcon({}) : undefined,
          subLabel: totalCostLabel,
          label: tt.name ?? "",
          triggerElement: (
            <HStack
              gap={3}
              alignItems="center"
              flex={1}
              overflow="hidden"
              justifyContent={"space-between"}
            >
              {transportationIcon ? transportationIcon({}) : undefined}

              <HStack gap={2} flex={1}>
                <Text variant="mainSmallSemiBold" numberOfLines={1}>
                  {rate.name} - {tt.name}
                </Text>

                <Text variant="mainBaseMedium" color={"text-secondary"}>
                  {totalCostLabel}
                </Text>
              </HStack>
            </HStack>
          ),
        };
      });

      return {
        label: rate.name ?? "",
        options: transportationTypeOptions,
      };
    });

  return bundledOptions;
};

export const useGetBundledRateOptionsQueryString = graphql`
  query useGetBundledRateOptionsQuery(
    $timeSlotId: ID!
    $membershipTypeId: ID
    $visibleToMembershipTypeId: ID
    $rateType: RateType!
    $nonPlayingHost: Boolean
    $transportationMemberId: ID
    $transportationPlayerType: PlayerType
  ) {
    slot: node(id: $timeSlotId) {
      __typename
      ... on Slot {
        rates: playerRates(
          type: $rateType
          nonPlayingHost: $nonPlayingHost
          visibleToMembershipTypeId: $visibleToMembershipTypeId
        ) {
          rate {
            id
            name
            visibility
            appliesToMembershipType(membershipTypeId: $membershipTypeId)
          }
          nineHolePrice: totalGolfPrice(holeCount: NINE)
          eighteenHolePrice: totalGolfPrice(holeCount: EIGHTEEN)
        }
        transportationTypes(
          memberId: $transportationMemberId
          memberVisible: true
          playerType: $transportationPlayerType
        ) {
          id
          code
          customCode
          name
          enabled
          nineHolePrice: totalPrice(holeCount: NINE)
          eighteenHolePrice: totalPrice(holeCount: EIGHTEEN)
        }
        agenda {
          facility {
            club {
              supportsPrivate
            }
          }
        }
      }
    }
  }
`;
